import { Modal } from '@/components/dialogs/Modal';
import { AsyncButton } from '@/components/common/AsyncButton';
import { fmtRate } from './helpers';
import { MarginExposureMode, MarginRateApplyType, type MarginRatePreviewRowDto } from '@/api/types/marginControl';

interface Props {
  rows: MarginRatePreviewRowDto[];
  mode: number;
  applyType: number;
  rate: number;
  applyEnabled: boolean;
  onConfirm: () => Promise<void> | void;
  onClose: () => void;
}

export function ReviewDialog({ rows, mode, applyType, rate, applyEnabled, onConfirm, onClose }: Props) {
  const typeLabel = applyType === MarginRateApplyType.Multiply ? 'Margin Multiply' : 'Margin Rate Set';
  const modeLabel = mode === MarginExposureMode.NewAndExisting ? 'Initial and Maintenance' : 'Initial';
  const staleCount = rows.filter((r) => r.stale).length;
  const bothCols = mode === MarginExposureMode.NewAndExisting;
  const marginHeader = bothCols ? '1-lot Initial and Maintenance' : '1-lot Initial Margin';

  const firstFail = rows.find((r) => !r.marginCheckSuccess && r.marginCheckMessage);
  const failCount = rows.filter((r) => !r.marginCheckSuccess && r.marginCheckMessage).length;

  const marginCell = (r: MarginRatePreviewRowDto) => {
    if (r.staleReason) return <span className="text-warning">{r.staleReason}</span>;
    if (r.marginForOneLotCurrent == null && r.marginForOneLotProposedEstimate == null) return <span className="text-muted">Unavailable</span>;
    return `${fmtRate(r.marginForOneLotCurrent)} → ${fmtRate(r.marginForOneLotProposedEstimate)} ${r.marginCurrency}`.trim();
  };

  return (
    <Modal
      show
      title="Review margin rate changes"
      size="xl"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-light btn-sm" onClick={onClose}>Close</button>
          {applyEnabled && (
            <AsyncButton className="btn btn-success btn-sm" icon="fa fa-check" onClick={onConfirm}>Apply</AsyncButton>
          )}
        </>
      }
    >
      <div className="mb-2">
        <strong>{typeLabel}</strong> · Value <strong>{rate}</strong> · {modeLabel} · 1 lot Buy
        {staleCount > 0 && <span className="text-warning"> · {staleCount} stale</span>}
      </div>
      <div className="table-responsive">
        <table className="table dataTable table-striped table-hover w-100 mb-0 text-nowrap">
          <thead>
            <tr>
              <th className="text-start">Group</th>
              <th className="text-start">Path</th>
              <th className="text-start">Symbol</th>
              <th className="text-end">Init Buy</th>
              {bothCols && <th className="text-end">Maint Buy</th>}
              <th className="text-end" title="Estimated 1-lot margin (scaled; not a live MT5 simulation).">{marginHeader}</th>
              <th className="text-start">Ex. login</th>
              <th className="text-start">Leverage</th>
              <th className="text-start">Margin calc mode</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.rowKey || `${r.group}|${r.path}`} className={r.stale ? 'table-warning' : undefined}>
                <td className="text-start">{r.group}</td>
                <td className="text-start">{r.path}</td>
                <td className="text-start">{r.resolvedSymbol || '—'}</td>
                <td className="text-end">{fmtRate(r.effectiveInitialBuy)} → {fmtRate(r.proposedInitialBuy)}</td>
                {bothCols && <td className="text-end">{fmtRate(r.effectiveMaintenanceBuy)} → {fmtRate(r.proposedMaintenanceBuy)}</td>}
                <td className="text-end">{marginCell(r)}</td>
                <td className="text-start">
                  {r.previewLoginUsed ?? '—'}
                  {r.previewLoginAutoSelected && <span className="badge text-bg-light ms-1">auto</span>}
                </td>
                <td className="text-start">{r.loginLeverage != null ? `1:${r.loginLeverage}` : '—'}</td>
                <td className="text-start">{r.symbolCalcModeName || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {firstFail && (
        <div className="text-danger small mt-2">
          {firstFail.marginCheckMessage}
          {failCount > 1 && ` (+${failCount - 1} more)`}
        </div>
      )}
    </Modal>
  );
}
